import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/AuthContext";
import { useCalls } from "@/CallsContext";
import { api, apiErrorMessage } from "@/lib/api";
import { openPaddleCheckout } from "@/lib/paddleCheckout";
import { customerSubscriptionBadge, isManualAccess } from "@/lib/subscriptionDisplay";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function paddleEventName(event) {
  return String(event?.name || event?.type || "").toLowerCase();
}

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const { license, loading, refreshLicense } = useCalls();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paddleBusy, setPaddleBusy] = useState(false);
  const [error, setError] = useState("");
  const [checkoutClosed, setCheckoutClosed] = useState(false);
  const subscribeStarted = useRef(false);
  const hasAccess = license?.active === true || license?.has_access === true || user?.has_access === true;
  const badge = customerSubscriptionBadge(license);
  const manualAccess = isManualAccess(license) || isManualAccess(user);

  const waitForAccess = useCallback(async () => {
    for (let i = 0; i < 8; i += 1) {
      const [freshUser, freshLicense] = await Promise.all([
        refreshUser(),
        refreshLicense(),
      ]);
      if (freshUser?.has_access === true || freshLicense?.has_access === true || freshLicense?.active === true) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    return false;
  }, [refreshLicense, refreshUser]);

  const onPaddleEvent = useCallback(async (event) => {
    const name = paddleEventName(event);
    if (name === "checkout.completed") {
      setCheckoutClosed(false);
      await waitForAccess();
      return;
    }
    if (name === "checkout.closed" || name === "checkout.abandon") {
      setCheckoutClosed(true);
      await refreshUser();
      await refreshLicense();
    }
  }, [refreshLicense, refreshUser, waitForAccess]);

  const onPayWithPaddle = useCallback(async () => {
    setError("");
    setCheckoutClosed(false);
    setPaddleBusy(true);
    try {
      const res = await api.get("/billing/paddle/checkout");
      await openPaddleCheckout(res.data, { onEvent: onPaddleEvent });
    } catch (err) {
      setError(apiErrorMessage(err) || "Could not start Paddle checkout.");
    } finally {
      setPaddleBusy(false);
    }
  }, [onPaddleEvent]);

  useEffect(() => {
    if (subscribeStarted.current || loading || paddleBusy || hasAccess) return;
    if (searchParams.get("subscribe") !== "1") return;
    subscribeStarted.current = true;
    setSearchParams({}, { replace: true });
    onPayWithPaddle();
  }, [hasAccess, loading, onPayWithPaddle, paddleBusy, searchParams, setSearchParams]);

  if (!hasAccess) {
    return (
      <div className="mpd-page">
        <div className="mpd-page-head">
          <div>
            <p className="mpd-kicker">Complete checkout</p>
            <h1>Start your 7-day free trial</h1>
            <p className="mpd-sub">MinutePay stays locked until Paddle confirms your trial. Closing checkout does not unlock the app.</p>
          </div>
        </div>
        <article className="mpd-card">
          <p className="mpd-plan-price">$9.99 USD/month</p>
          <p className="mpd-plan-note">
            Payment method required. You won&apos;t be charged today. After your 7-day trial, you&apos;ll be charged $9.99/month. Cancel anytime before renewal.
          </p>
          {checkoutClosed ? (
            <p className="mp-error">Checkout was closed before a payment method was added. Complete Paddle checkout to access MinutePay.</p>
          ) : null}
          {error ? <p className="mp-error">{error}</p> : null}
          <div className="mpd-pay-actions">
            <Button
              className="mpd-primary"
              type="button"
              disabled={paddleBusy || loading}
              onClick={onPayWithPaddle}
            >
              {paddleBusy ? "Opening Paddle…" : "Start 7-Day Free Trial"}
            </Button>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="mpd-page">
      <div className="mpd-page-head">
        <div>
          <p className="mpd-kicker">Billing</p>
          <h1>Subscription</h1>
          <p className="mpd-sub">
            {manualAccess
              ? "Manual access is active for a fixed 30-day window. It does not renew automatically."
              : "Payment method required. You won&apos;t be charged today."}
          </p>
        </div>
      </div>

      <article className="mpd-card">
        <div className="mpd-card-head">
          <div>
            <p>Plan</p>
            <h2>MinutePay Basic</h2>
          </div>
          <span className={`mpd-license ${hasAccess ? "is-on" : "is-off"}`}>
            <strong>{loading ? "Loading…" : badge.title}</strong>
            {!loading && badge.subtitle ? <em>{badge.subtitle}</em> : null}
          </span>
        </div>
        <p className="mpd-plan-price">$9.99 USD/month</p>
        <p className="mpd-plan-note">
          {manualAccess
            ? `Access source: Manual Access. Expires ${formatDate(license?.manual_access_expires_at || user?.manual_access_expires_at)}.`
            : "After your 7-day trial, you'll be charged $9.99/month. Cancel anytime before renewal."}
        </p>

        <dl className="mpd-dl">
          <div>
            <dt>Status</dt>
            <dd>{loading ? "Loading…" : badge.title}</dd>
          </div>
          <div>
            <dt>{manualAccess ? "Expires" : "Renews"}</dt>
            <dd>
              {loading
                ? "Loading…"
                : manualAccess
                  ? formatDate(license?.manual_access_expires_at || user?.manual_access_expires_at)
                  : badge.subtitle
                    ? badge.subtitle.replace(/^Renews /, "")
                    : "—"}
            </dd>
          </div>
          <div>
            <dt>Paid license</dt>
            <dd>{license?.license_status || user?.license_status || "—"}</dd>
          </div>
          <div>
            <dt>Trial</dt>
            <dd>
              {license?.license_status === "active"
                ? "—"
                : (license?.trial_status || user?.trial_status || "none") === "active"
                  ? `Active until ${formatDate(license?.trial_ends_at || user?.trial_ends_at)}`
                  : (license?.trial_status || user?.trial_status || "none")}
            </dd>
          </div>
        </dl>

        {error ? <p className="mp-error">{error}</p> : null}

        {manualAccess ? null : (
          <div className="mpd-pay-actions">
            <Button
              className="mpd-primary"
              type="button"
              disabled={paddleBusy || loading}
              onClick={onPayWithPaddle}
            >
              {paddleBusy
                ? "Opening Paddle…"
                : license?.license_status === "active"
                  ? "Subscribe"
                  : "Start 7-Day Free Trial"}
            </Button>
          </div>
        )}
      </article>
    </div>
  );
}
